<template>
  <div :class="['ui-input', classList]" @click="focus">
    <label v-if="label">
      {{ label }}
    </label>
    <div class="ui-input-input">
      <transition name="fade">
        <div v-if="$slots.prepend" class="ui-input-prepend">
          <slot name="prepend" />
        </div>
      </transition>
      <input
        ref="input"
        v-bind="$attrs"
        :autocomplete="autocomplete !== 'on' && 'nope'"
        :value="computedValue"
        @input="update($event.target.value)"
        :disabled="disabled"
        :readonly="readonly"
        @keydown="onKeyDown"
        @focus="onFocus"
        @blur="onBlur"
      >
      <transition name="fade">
        <ui-loader v-if="loading" />
        <div
          v-else-if="showClear"
          class="ui-input-append"
          @click="clear"
        >
          <f-sprite-icon :icon-link="$options.icons.IconClose.id" />
        </div>
        <div v-else-if="$slots.append" class="ui-input-append">
          <slot name="append" />
        </div>
      </transition>
    </div>
    <p class="error" v-if="$slots.error">
      <slot name="error" />
    </p>
    <p class="error" v-else-if="computedError && computedError.length">
      {{ computedError }}
    </p>
    <slot name="after" />
  </div>
</template>

<script>
import IconClose from '../../../assets/icons/close.svg';
import validators from './validators';

export default {
  name: 'ui-input',

  icons: {
    IconClose
  },

  props: {
    value: {
      type: [String, Number],
      default: ''
    },

    label: {
      type: String,
      default: ''
    },

    mask: {
      type: String,
      default: ''
    },

    error: {
      type: [String, Boolean],
      default: ''
    },

    disabled: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    },

    loading: {
      type: Boolean,
      default: false
    },

    autofocus: {
      type: Boolean,
      default: false
    },

    autocomplete: {
      type: String,
      default: 'on'
    },

    clearable: {
      type: Boolean,
      default: false,
    },

    large: {
      type: Boolean,
      default: false
    },

    validate: {
      type: [Array, String],
      default: () => ([]),
    },

    numbers: {
      type: Boolean,
      default: false,
    }
  },

  data: () => ({
    focused: false,
    validationError: '',
    lastKeyCode: null,
    forceRecompute: 0,
  }),

  computed: {
    classList() {
      return [
        {
          error: !!this.computedError,
          large: this.large,
          disabled: this.disabled,
          readonly: this.readonly,
          focused: this.focused && !this.readonly,
          code: this.mask === 'code'
        }
      ];
    },

    validators() {
      if (typeof this.validate === 'string') {
        return validators[this.validate] || [];
      }
      return this.validate
        .map(r => validators[r])
        .filter(i => !!i)
        .flat();
    },

    computedError() {
      return this.loading ? '' : this.error || this.validationError;
    },

    currentMask() {
      switch (this.mask) {
        case 'phone': return '+7 (9##) ###-##-##';
        case 'date': return '##.##.####';
        case 'interval': return '##.##.#### - ##.##.####';
        case 'code': return '######'
        default: return this.mask.includes('#') ? this.mask : '';
      }
    },

    computedValue() {
      return this.currentMask && this.forceRecompute >= 0 && this.value
        ? this.$options.filters.mask(this.value, this.currentMask) // маскируем только при маске
        : this.value; // без маски
    },

    showClear() {
      return this.clearable && this.value && this.focused;
    },

    valid() {
      // цепляемся по рефу сверху сюда
      // например для дизейбла кнопки сабмита
      return !this.checkValid();
    },
  },

  mounted() {
    this.autofocus && this.focus();
  },

  methods: {
    onKeyDown(e) {
      if (!this.currentMask) return;
      this.lastKeyCode = e.keyCode;
      if (e.keyCode === 8) {
        e.preventDefault();
        this.onBackSpace();
      }
    },

    // prevented. удаляем руками для правильной обработки курсора (всгда --)
    onBackSpace() {
      const i = this.$refs.input.selectionStart;
      i > 0 && this.update(this.computedValue.slice(0, i - 1) + this.computedValue.slice(i));
    },
    update(value) {
      if (this.numbers) {
        value = value.replace(/[\D]/g, '');
      }
      if (this.currentMask) {
        if (this.mask === 'phone' && value[0] === '8') {
          value = '+7' + value.slice(1);
        }
        value = this.$options.filters.mask(value, this.currentMask);
        this.fixMaskedCursor(this.value !== value);
        value = this.outputValue(value);
      }

      // принудительно пересчитываем инпут, иначе не видит обновления на инпуте
      // и не обновляется v-model, остаются незамаскированные символы
      if (value === this.value) {
        this.forceRecompute++;
        return;
      }
      this.validationError = '';
      this.$emit('input', value);

      this.$nextTick(() => {
        this.$emit('is-valid', this.valid); // возвращает статус валидности по каждому нажатию
        this.valid && this.$emit('on-valid'); // отрабатывает, когда введенное валидно
        value.length === this.currentMask.length && this.$emit('mask-completed', value);
      });
    },
    outputValue(v) {
      return this.mask === 'phone' ? v.replace(/\D/g, '') : v;
    },

    fixMaskedCursor(valueChanged = true) {
      let index = this.$refs.input.selectionStart;
      switch (this.lastKeyCode) {
        case 8: // backspace
          index--;
          break;
        case 46: // delete
          break;
        default:
          valueChanged ? index += this.currentMask.slice(index - 1).indexOf('#') : index--;
          break;
      }
      // тут 2 nextTick, чтобы получить и обновить значение из v-model
      this.$nextTick(() => this.setCursorPosition(index));
    },

    setCursorPosition(pos) {
      this.$nextTick(() => this.$refs.input.setSelectionRange(pos, pos));
    },

    // для внешннего вызова
    checkValidOutside() {
      this.validationError = this.checkValid();
      return this.valid;
    },

    checkValid() {
      if (!this.validators.length) return;
      let error = '';
      this.validators.some(rule => {
        if (typeof rule === 'function') {
          error = rule(this.computedValue);
        } else {
          const [regex, err] = rule;
          if (!(new RegExp(regex)).test(this.computedValue)) {
            error = err;
          }
        }
        return !!error;
      });

      return error;
    },

    focus() {
      this.$refs.input.focus();
    },

    blur() {
      this.$refs.input.blur();
    },

    clear() {
      this.update('');
      this.focus();
    },
    onFocus() {
      this.focused = true;
      this.$nextTick(() => this.validationError = '');
      this.$emit('focus');
    },
    onBlur() {
      document.addEventListener('mouseup', this.blurComplete);
    },
    blurComplete() {
      document.removeEventListener('mouseup', this.blurComplete);
      this.validationError = this.checkValid();
      this.focused = false;
      this.$emit('blur');
    },
  }
};
</script>

<style lang="scss">
.ui-input {
  position: relative;
  min-height: 84px; // 64 + запас на ошибку 20
  label,
  p.error {
    display: block;
    font-size: 12px;
    line-height: 20px;
  }

  label {
    margin-bottom: 4px;
    color: $gray;
  }

  p.error {
    margin-top: 6px;
    color: $red;
  }

  &-input {
    position: relative;
    width: 100%;
    height: 40px;
    padding: 1px 12px;
    border: 1px solid $gray_lu;
    border-radius: 4px;
    overflow: hidden;
    transition: .2s ease border-color;
    display: flex;
    align-items: center;
    background: $white;
  }

  &.error:not(.disabled) .ui-input-input {
    border-color: $red !important;
  }

  &.large {
    .ui-input-input {
      height: 56px;

      & > input {
        font-size: 16px;
        line-height: 24px;
      }
    }
    .sprite-icon {
      width: 24px;
      height: 24px;
    }
  }

  &.readonly,
  &.disabled {
    .ui-input-input {
      background: $gray_bgr;
    }
    input {
      background-color: $gray_bgr;
      box-shadow: 0 0 0 1000px $gray_bgr inset;
    }
  }

  &.disabled {
    pointer-events: none;

    p.error {
      display: none;
    }

    input {
      color: $gray_l;
    }
  }

  &.readonly {
    pointer-events: auto;

    .ui-input-append svg {
      color: $black;
    }
  }

  &.focused {
    .ui-input-input {
      border-color: $black;
    }
  }

  &.code {
    input {
      letter-spacing: 4px;
    }
  }

  input {
    flex: 1;
    width: 100%;
    height: 100%;
    padding: 0;
    font-size: 14px;
    line-height: 20px;
    color: $black;
    border: none;
    outline: none;
    box-shadow: 0 0 0 1000px $white inset;
    /* stylelint-disable-next-line */
    &::-webkit-input-placeholder {
      color: $gray;
      line-height: 20px;
      letter-spacing: normal;
      transition: .2s color ease;
      &:focus {
        color: transparent;
      }
    }
  }

  &-prepend {
    margin-right: 12px;
  }
  &-append {
    margin-left: 12px;
    cursor: pointer;

    svg.sprite-icon {
      width: 16px;
      height: 16px;
      color: $gray_l;
    }
  }

  .ui-loader {
    color: $gray;
  }
}
@include breakpoint("desktop") {
  .ui-input {

    .ui-input-input:hover {
      border-color: $gray_l;
    }

    &.focused {
      .ui-input-input:hover {
        border-color: $black;
      }
    }

    &.readonly {
      .ui-input-input:hover {
        border-color: $gray_lu;
      }
    }
  }
}
</style>
